import React, { useCallback, useRef, useState } from "react";

import {
  ArticleEditSVG,
  BookmarkHollowSVG,
  Button,
  DetailsSVG,
  ErrorFilledSVG,
  Icon,
  Logo,
  Modal,
} from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { lock, clearBodyLocks } from "tua-body-scroll-lock";

import { actionNames, withTrackEvent } from "@newsela/snitch";
import { PREMIUM_TRIAL_CAMPAIGN_SLUG } from "@/constants/TRIAL_LICENSE_CAMPAIGNS";
import CloseIcon from "@/icons/CloseIcon";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import useTrialCampaignPost from "@/users/useTrialCampaignPost";
import {
  baseStyles,
  ctaButtonCss,
  ctaButtonContainer,
  closeButtonCss,
  errorContainer,
  errorIconCss,
  footerContainer,
  headerContainer,
  headingCss,
  heroSvgCss,
  iconCss,
  listCss,
  listItemCss,
  scrollableContainer,
  svgContainer,
  textContainer,
} from "./TrialModal.style";
import NewselaPremiumExperienceSVG from "./NewselaPremiumExperience.svg.jsx";

const ButtonWithTrackEvent = withTrackEvent(Button);

const LABEL_ID = "trial-modal";

const useRefWithCallback = (onMount, onUnmount) => {
  const nodeRef = useRef(null);

  const setRef = useCallback(
    (node) => {
      if (nodeRef.current) {
        onUnmount(nodeRef.current);
      }

      nodeRef.current = node;
      if (nodeRef.current) {
        onMount(nodeRef.current);
      }
    },
    [onMount, onUnmount],
  );

  return setRef;
};

const trialModalProps = {
  ariaProps: PropTypes.object,
  closeModal: PropTypes.func,
  dataProps: PropTypes.objectOf(PropTypes.string),
  onBackgroundClick: PropTypes.func,
};

const TrialModal = (props) => {
  const { ariaProps, closeModal } = props;

  const router = useRouter();

  const [hasError, setHasError] = useState(false);
  const [createTrialCampaignPostRequest] = useTrialCampaignPost();
  const addUserToCampaignTrial = async () => {
    try {
      const response = await createTrialCampaignPostRequest({
        slug: PREMIUM_TRIAL_CAMPAIGN_SLUG,
      });
      if (response.error) {
        setHasError(true);
      } else {
        closeModal();
        router.push(MONOLITH_ORIGIN);
      }
    } catch {
      setHasError(true);
    }
  };

  const scrollRef = useRefWithCallback(lock, clearBodyLocks);

  const handleClick = () => {
    setHasError(false);
    addUserToCampaignTrial();
  };

  return (
    <Modal
      {...props}
      __cssFor={{
        root: {
          zIndex: 14,
        },
      }}
      ariaProps={{
        "aria-labelledby": LABEL_ID,
        ...ariaProps,
      }}
      onClick={closeModal}
    >
      <div css={baseStyles}>
        <div css={svgContainer}>
          <NewselaPremiumExperienceSVG aria-hidden css={heroSvgCss} />
        </div>
        <div css={textContainer}>
          <div css={headerContainer}>
            <Logo __overrideHeight="24px" ariaProps={{ role: "img" }} />
            <ButtonWithTrackEvent
              __cssFor={{ root: closeButtonCss }}
              ariaProps={{ "aria-label": "close" }}
              flavor={Button.flavors.incognito}
              onClick={closeModal}
              actionName={actionNames.cancel}
              streamProperties={{
                description: "Close trial modal",
              }}
            >
              <CloseIcon />
            </ButtonWithTrackEvent>
          </div>
          <div css={scrollableContainer} ref={scrollRef} tabIndex="0">
            <h1 id={LABEL_ID} css={headingCss}>
              Unlock more of what{"\n"}
              Newsela has to offer
            </h1>
            <p>
              For <b>45 days</b> you'll have access to Newsela's 3 premium
              products for ELA, social studies, and science.{" "}
            </p>
            <ul role="list" css={listCss}>
              <li css={listItemCss}>
                <Icon
                  SvgComponent={DetailsSVG}
                  isDecorative
                  __cssFor={{ root: iconCss }}
                />
                Access teaching resources like curations, unit guides, and
                graphic organizers.
              </li>
              <li css={listItemCss}>
                <Icon
                  SvgComponent={ArticleEditSVG}
                  isDecorative
                  __cssFor={{ root: iconCss }}
                />
                Create more impactful assignments and see insights into student
                work.
              </li>
              <li css={listItemCss}>
                <Icon
                  SvgComponent={BookmarkHollowSVG}
                  isDecorative
                  __cssFor={{ root: iconCss }}
                />
                Explore over 15,000 premium texts and videos with your students.
              </li>
            </ul>
            <p>
              Love it? You can request a trial for your entire school at any
              time—without ever sharing personal payment information.
            </p>
          </div>
          <div css={footerContainer}>
            <div css={ctaButtonContainer}>
              <ButtonWithTrackEvent
                __cssFor={{ root: ctaButtonCss }}
                flavor={Button.flavors.primary}
                size={Button.sizes.lg}
                onClick={handleClick}
                actionName="Create"
                streamProperties={{
                  description: "Add user to campaign trial",
                }}
              >
                Activate trial
              </ButtonWithTrackEvent>
            </div>
            {hasError && (
              <div css={errorContainer} aria-live="polite">
                <Icon
                  SvgComponent={ErrorFilledSVG}
                  isDecorative
                  __cssFor={{ root: errorIconCss }}
                />
                An error has occurred.
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

TrialModal.propTypes = trialModalProps;

export default TrialModal;
